import { computed, ref, toValue, watch, type MaybeRefOrGetter } from "vue";
import type { ProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { SiteSearchResult } from "~/types/search";
import {
	recommendationKey,
	recommendationLimit,
	selectRecommendedProducts,
	type RecommendationContext,
	type RecommendationFallback
} from "~/utils/product/recommendations";

export type { RecommendationContext, RecommendationFallback };

export interface UseProductRecommendationsOptions {
	/** Hello Retail product box key, from the Storyblok blok. */
	productBoxKey: MaybeRefOrGetter<string | undefined>;
	context: MaybeRefOrGetter<RecommendationContext>;
	/** Storyblok Number fields arrive as strings; coerced below. */
	limit?: MaybeRefOrGetter<number | string | undefined>;
	meili?: MaybeRefOrGetter<RecommendationFallback | undefined>;
	/**
	 * Distinguishes two surfaces that would otherwise share a cache key — the
	 * PDP block and the mini cart on the same page.
	 */
	scope: string;
	/**
	 * Hold the request back until this turns true, for a surface the visitor
	 * may never open. Defaults to fetching as soon as the component mounts.
	 */
	enabled?: MaybeRefOrGetter<boolean>;
}

/**
 * Hello Retail first, Meilisearch as the fallback. The caller supplies the
 * context, so the PDP block, a category page and the mini cart share one
 * implementation. Client-only (`server: false`) — below the fold everywhere.
 */
export const useProductRecommendations = (
	options: UseProductRecommendationsOptions
) => {
	const {
		public: {
			norce: { searchEngine }
		}
	} = useRuntimeConfig();
	const { localeProperties } = useI18n();
	const { helloRetailTrack } = useHelloRetailTracking();

	const limit = computed(() => recommendationLimit(toValue(options.limit)));
	const productBoxKey = computed(() => toValue(options.productBoxKey));
	const context = computed(() => toValue(options.context));
	const meili = computed(() => toValue(options.meili));
	const isEnabled = computed(() => toValue(options.enabled) ?? true);

	const contextKey = computed(() =>
		recommendationKey({
			scope: options.scope,
			iso: localeProperties.value.iso,
			productBoxKey: productBoxKey.value,
			context: context.value,
			limit: limit.value
		})
	);

	// Hello Retail path — only when it is the configured engine.
	const { data: recoms, execute: fetchRecoms } =
		searchEngine === "helloretail"
			? useLazyAsyncData(
					() => `recoms:${contextKey.value}`,
					() =>
						$fetch("/api/recoms", {
							method: "POST",
							body: {
								context: context.value,
								productBoxKey: productBoxKey.value
							},
							headers: { "Content-Type": "application/json" }
						}),
					{ server: false, immediate: isEnabled.value }
				)
			: { data: ref(null), execute: async () => {} };

	// Meilisearch path. Fires only when HR is not configured (Meili is primary)
	// or when HR returned nothing — otherwise its response would just be
	// discarded by `products` below, at the cost of a Norce roundtrip.
	const { data: meiliRecoms, execute: fetchMeili } = useLazyAsyncData(
		() => `meili-recoms:${contextKey.value}`,
		() =>
			$fetch<{ products: SiteSearchResult[] }>(
				"/api/search/meilisearch-recommendations",
				{
					query: {
						partNo: meili.value?.partNo || "",
						brand: meili.value?.brand || "",
						category: meili.value?.category || "",
						strategy: meili.value?.strategy || "brand_and_category",
						limit: limit.value,
						culture: localeProperties.value.iso
					}
				}
			),
		{
			server: false,
			immediate:
				isEnabled.value && !!meili.value && searchEngine !== "helloretail"
		}
	);

	// When HR is the engine, hold the fallback back until HR's outcome is known.
	if (searchEngine === "helloretail") {
		watch(recoms, (result) => {
			if (!meili.value || !result) return;
			const ok = (result as { success?: boolean }).success;
			const list = (result as { products?: unknown[] }).products;
			if (!ok || !list?.length) void fetchMeili();
		});
	}

	// A surface that starts disabled (the mini cart) fetches the moment it opens,
	// once per basket, and only with a product box to ask.
	watch(isEnabled, (enabled) => {
		if (!enabled) return;
		if (productBoxKey.value && !recoms.value) void fetchRecoms();
		if (meili.value && searchEngine !== "helloretail") void fetchMeili();
	});

	const products = computed<ProductItem[]>(() =>
		selectRecommendedProducts({
			searchEngine,
			helloRetail: recoms.value,
			meilisearch: meiliRecoms.value,
			priceListId: useProductStore()?.priceLists?.[0]?.Id
		})
	);

	/**
	 * Hello Retail's click attribution. Routed through `useHelloRetailTracking()`
	 * rather than `helloRetailApi.track()`: this is analytics, so it is
	 * consent-gated and mirrored onto the dataLayer for GTM (AGENTS.md rule #4).
	 */
	const trackProductClick = (product: Partial<ProductItem>) => {
		const source = (product as { trackingCode?: string }).trackingCode;
		if (!source) return;

		helloRetailTrack({
			serviceType: "click",
			trackingData: { source }
		});
	};

	return { products, trackProductClick };
};
